import { useState } from "react";
import type { ReactElement } from "react";
import type { TicketDetail } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBestaetigung } from "@kundeninfo/ui";
import { Optionen } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import { renderMarkdown } from "../../markdown.js";
import { formatDatum } from "../../format.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import type { ReiterBasis } from "./TicketDialogTeile.js";

/**
 * DER REITER „VERLAUF“ (F10, F11): Beschreibung (Teil des Kopfs, gespeichert mit „Speichern“
 * im Fuß), Kommentare und Ereignisse in zeitlicher Folge, neuer Kommentar sofort gespeichert.
 * Markdown immer über `renderMarkdown` ohne rohes HTML. Eigene Kommentare darf jeder
 * bearbeiten und löschen, fremde nur ADMIN („Rechte“) — sonst stehen die Knöpfe gar nicht da;
 * eine 403 des Servers erscheint als Hinweis im Panel.
 */

type Kommentar = TicketDetail["teile"]["comments"][number];
type Ereignis = TicketDetail["events"][number];

/** Ein Kalendertag aus dem Ereignis als TT.MM.JJJJ (Sichtprobe 13), nie als ISO. */
function tag(wert: unknown, leer: string): string {
  return typeof wert === "string" && wert !== "" ? formatDatum(wert) : leer;
}

const EREIGNIS_TEXT: Record<string, (d: Record<string, unknown>) => string> = {
  CREATED: () => "angelegt",
  TITLE_CHANGED: (x) => `Titel: „${String(x.von)}“ → „${String(x.nach)}“`,
  DESCRIPTION_CHANGED: () => "Beschreibung geändert",
  COLUMN_CHANGED: (x) => `Spalte: ${String(x.von)} → ${String(x.nach)}`,
  RESUBMIT_CHANGED: (x) => `Wiedervorlage: ${tag(x.von, "keine")} → ${tag(x.nach, "keine")}`,
  RESUBMIT_TIME_CHANGED: (x) => `Uhrzeit: ${String(x.von ?? "keine")} → ${String(x.nach ?? "keine")}`,
  ASSIGNEE_CHANGED: (x) => `Zuständig: ${String(x.von ?? "niemand")} → ${String(x.nach ?? "niemand")}`,
  CUSTOMER_ADDED: (x) => `Kunde zugeordnet: ${String(x.name)}`,
  CUSTOMER_REMOVED: (x) => `Kunde gelöst: ${String(x.name)}`,
  CONTACT_ADDED: (x) => `Ansprechpartner zugeordnet: ${String(x.name)}`,
  CONTACT_REMOVED: (x) => `Ansprechpartner gelöst: ${String(x.name)}`,
  REFERENCE_ADDED: (x) => `Bezug: ${String(x.label)}`,
  REFERENCE_REMOVED: (x) => `Bezug gelöst: ${String(x.label)}`,
  LINK_ADDED: (x) => `Verknüpfung: ${String(x.label ?? "")}`,
  LINK_REMOVED: (x) => `Verknüpfung gelöst: ${String(x.label ?? "")}`,
  ATTACHMENT_ADDED: (x) => `Anhang: ${String(x.name)}`,
  ATTACHMENT_REMOVED: (x) => `Anhang gelöscht: ${String(x.name)}`,
  SUBTASK_ADDED: (x) => `Teilaufgabe: ${String(x.titel)}`,
  SUBTASK_DONE: (x) => `Teilaufgabe erledigt: ${String(x.titel)}`,
  SUBTASK_REOPENED: (x) => `Teilaufgabe wieder offen: ${String(x.titel)}`,
  SUBTASK_REMOVED: (x) => `Teilaufgabe gelöscht: ${String(x.titel)}`,
  LINK_STATE_CHANGED: (x) => `${String(x.label ?? "Verknüpfung")}: ${String(x.von ?? "?")} → ${String(x.nach ?? "?")}`,
  TEAM_CHANGED: (x) => teamWechselText(x),
};

/** F133: „Team: allgemein → BH (privat), entfernt: Zuständig Anna; Beobachter Bernd". */
function teamWechselText(x: Record<string, unknown>): string {
  const nach = typeof x.nach === "string" ? `${x.nach} (privat)` : "allgemein";
  const weg = (x.entfernt ?? {}) as { zustaendig?: unknown; beobachter?: unknown };
  const teile = [
    typeof weg.zustaendig === "string" ? `Zuständig ${weg.zustaendig}` : "",
    Array.isArray(weg.beobachter) && weg.beobachter.length > 0 ? `Beobachter ${weg.beobachter.map(String).join(", ")}` : "",
  ].filter((t) => t !== "");
  return `Team: ${typeof x.von === "string" ? x.von : "allgemein"} → ${nach}${teile.length > 0 ? `, entfernt: ${teile.join("; ")}` : ""}`;
}

const ZEIT = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit",
  year: "numeric", hour: "2-digit", minute: "2-digit" });
const zeit = (iso: string): string => ZEIT.format(new Date(iso));

function ereignisText(e: Ereignis): string {
  const text = EREIGNIS_TEXT[e.eventType]?.(e.detail) ?? e.eventType;
  return `${e.user?.name ?? "System"}: ${text}`;
}

/** Vorschau eines Markdown-Entwurfs — nur bei Inhalt. */
function Vorschau(p: { text: string }): ReactElement | null {
  if (p.text.trim() === "") return null;
  return (
    <div className="td__vorschau">
      <span className="td__neben">Vorschau</span>
      {renderMarkdown(p.text)}
    </div>
  );
}

export function VerlaufReiter(p: ReiterBasis & {
  ich: { id: string; istAdmin: boolean };
  beschreibung: string; beschreibungGesperrt: boolean; beschreibungFehler: string | undefined;
  onBeschreibung: (w: string) => void;
  /** Entwurf „Neuer Kommentar“ — lebt im Dialog (Reiterwechsel, Rückfrage beim Abbrechen). */
  entwurf: string; onEntwurf: (w: string) => void;
}): ReactElement {
  const [ereignisse, setEreignisse] = useState(true);
  const [beschreibungOffen, setBeschreibungOffen] = useState(false);
  const neu = p.entwurf;
  const [bearbeite, setBearbeite] = useState<Kommentar | null>(null);
  const weg = useSchreibweg(p.onNeu, { nachladen: p.nachladen, felder: ["contentMarkdown"] });
  const { bestaetigen, bestaetigung } = useBestaetigung();
  const eintraege = [
    ...p.d.teile.comments.map((c) => ({ art: "k" as const, zeit: c.createdAt, c })),
    ...(ereignisse ? p.d.events.map((e) => ({ art: "e" as const, zeit: e.occurredAt, e })) : []),
  ].sort((a, b) => a.zeit.localeCompare(b.zeit));

  async function kommentieren(): Promise<void> {
    const text = neu;
    if (await weg.schreiben(() => p.client.addTicketComment(p.d.id, text), "Der Kommentar ließ sich nicht speichern.")) {
      p.onEntwurf("");
    }
  }

  async function loeschen(c: Kommentar): Promise<void> {
    if (!(await bestaetigen({ titel: "Kommentar löschen?", text: `Den Kommentar von ${c.authorName} endgültig löschen?`,
                              bestaetigenText: "Löschen", gefaehrlich: true }))) return;
    await weg.schreiben(() => p.client.deleteTicketComment(p.d.id, c.id), "Der Kommentar ließ sich nicht löschen.");
  }

  const offenBeschreibung = beschreibungOffen || p.beschreibungFehler !== undefined;

  return (
    <div className="td__verlauf">
      <section className="td__abschnitt" aria-label="Beschreibung">
        <div className="td__abschnittkopf">
          <h4 className="td__ueberschrift">Beschreibung</h4>
          <KiKnopf art="leise" ausgeklappt={offenBeschreibung} deaktiviert={p.beschreibungFehler !== undefined}
            onClick={() => setBeschreibungOffen(!beschreibungOffen)}>
            {offenBeschreibung ? "Bearbeiten schließen" : "Beschreibung bearbeiten"}
          </KiKnopf>
        </div>
        {offenBeschreibung && (
          <Feld id="td-beschreibung" label="Beschreibung (Markdown)" typ="mehrzeilig" zeilen={6} wert={p.beschreibung}
            onChange={p.onBeschreibung} bearbeitbar max={20000} gesperrt={p.beschreibungGesperrt}
            fehler={p.beschreibungFehler} />
        )}
        <div className="td__beschreibung">
          {p.beschreibung.trim() === "" ? <p className="td__leer">Keine Beschreibung.</p> : renderMarkdown(p.beschreibung)}
        </div>
      </section>
      <Optionen mehrfach beschriftung="Anzeige" wert={ereignisse ? ["ja"] : []}
        onGeaendert={(e) => setEreignisse((e.werte ?? []).includes("ja"))}>
        <option value="ja">Ereignisse zeigen</option>
      </Optionen>
      <ol className="td__eintraege">
        {eintraege.map((x) => (x.art === "e"
          ? <li key={x.e.id} className="td__ereignis">{`${zeit(x.e.occurredAt)} · ${ereignisText(x.e)}`}</li>
          : (
            <li key={x.c.id} className="td__kommentar">
              <div className="td__kommentarkopf">
                <span className="td__verfasser">{x.c.authorName}</span>
                <span className="td__neben">
                  {zeit(x.c.createdAt)}{x.c.updatedAt !== x.c.createdAt ? " · bearbeitet" : ""}
                </span>
                {(x.c.authorUserId === p.ich.id || p.ich.istAdmin) && (
                  <span className="td__aktionen">
                    <KiKnopf art="leise" name={`Kommentar von ${x.c.authorName} bearbeiten`}
                      onClick={() => setBearbeite(x.c)}>Bearbeiten</KiKnopf>
                    <LoeschKnopf name={`Kommentar von ${x.c.authorName} löschen`} deaktiviert={weg.laeuft}
                      onClick={() => { void loeschen(x.c); }} />
                  </span>
                )}
              </div>
              <div className="td__inhalt">{renderMarkdown(x.c.contentMarkdown)}</div>
            </li>
          )))}
      </ol>
      <Feld id="td-kommentar" label="Neuer Kommentar" typ="mehrzeilig" wert={neu} bearbeitbar
        onChange={(w) => { weg.feldRaeumen("contentMarkdown"); p.onEntwurf(w); }}
        max={20000} fehler={weg.feldFehler.contentMarkdown} hilfe="Markdown: **fett**, _kursiv_, Listen" />
      <Vorschau text={neu} />
      <div className="td__aktionszeile">
        <KiKnopf deaktiviert={neu.trim() === "" || weg.laeuft} onClick={() => { void kommentieren(); }}>Kommentieren</KiKnopf>
      </div>
      <SchreibHinweis meldung={weg.meldung} />
      {bestaetigung}
      {bearbeite !== null && (
        <KommentarBearbeiten {...p} kommentar={bearbeite} onFertig={() => setBearbeite(null)} />
      )}
    </div>
  );
}

/** Das Panel „Kommentar bearbeiten“ (Hausregel Modal-Bearbeitung): eigener Schreibweg, damit
 *  Befund, 403 und 409 IM Panel stehen, wo gearbeitet wird; bei 409 bleibt der Text stehen
 *  und das nächste Speichern nimmt die nachgeladene Version. */
function KommentarBearbeiten(p: ReiterBasis & { kommentar: Kommentar; onFertig: () => void }): ReactElement {
  const [text, setText] = useState(p.kommentar.contentMarkdown);
  const weg = useSchreibweg(p.onNeu, { nachladen: p.nachladen, felder: ["contentMarkdown"] });
  const aktuell = p.d.teile.comments.find((c) => c.id === p.kommentar.id);
  const geaendert = text !== p.kommentar.contentMarkdown;

  async function abbrechen(): Promise<void> {
    if (weg.laeuft) return;
    if (await darfAbbrechen(geaendert)) p.onFertig();
  }
  async function speichern(): Promise<void> {
    if (aktuell === undefined) return;
    const ok = await weg.schreiben(() => p.client.updateTicketComment(p.d.id, aktuell.id, aktuell.version, text),
      "Der Kommentar ließ sich nicht speichern.");
    if (ok) p.onFertig();
  }

  return (
    <Modal titel="Kommentar bearbeiten" breite="mittel" onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={weg.laeuft}>
      <Feld id="td-kommentar-bearbeiten" label="Kommentar (Markdown)" typ="mehrzeilig" zeilen={8} wert={text}
        onChange={(w) => { weg.feldRaeumen("contentMarkdown"); setText(w); }} bearbeitbar max={20000}
        gesperrt={weg.laeuft} fehler={weg.feldFehler.contentMarkdown} />
      <Vorschau text={text} />
      {aktuell === undefined && <SchreibHinweis meldung={{ art: "warnung", text: "Dieser Kommentar wurde inzwischen gelöscht." }} />}
      <SchreibHinweis meldung={weg.meldung} />
      <ModalFuss speichernText="Speichern" speichernGesperrt={text.trim() === "" || weg.laeuft || aktuell === undefined}
        abbrechenGesperrt={weg.laeuft} onSpeichern={() => { void speichern(); }} onAbbrechen={() => { void abbrechen(); }} />
    </Modal>
  );
}
